import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  propertyCreateFormStepper,
  propertyFormStepper,
} from '@pages/hubs/Property/const/property-create-form-stepper';
import { PropertySelectFormatEnum } from '@pages/hubs/Property/enum/property-select-format-enum';
import { PropertyStepsEnum } from '@pages/hubs/Property/enum/property-steps-enum';
import { UserConfirmStatusEnum } from '@shared/enums/hubs/user-confirm-status-enum';
import { isListOver } from '@shared/functions/isListOver';
import { changeCoverPhoto } from '@shared/utils/hubs/changeCoverPhoto';

import { acceptedPropertyWS, addPropertyWS, getProperties, updatePropertyWS } from './property-thunk';

import type { RootMeta } from '@pages/hubs/Garage/store/types';
import type { GetHubsParams } from '@pages/hubs/Pets/store/pets-types';
import type { PropertyType, RootPropertyItemsDataStep } from '@pages/hubs/Property/store/property-types';
import type { AssignPeoplePermissionsEnum } from '@shared/enums/assignPeoplePermissions.enum';
import type { AssignPeopleSelectValueModel } from '@shared/models/assignPeopleSelectValue.model';
import type { FilterParams } from '@shared/models/hubs/hub-filters.model';
import type { FormStepper } from '@shared/models/hubs/hub-form-stepper.model';
import type { ItemUserModel } from '@shared/models/itemUser.model';
import type { MediaType } from '@shared/models/media.model';

const propertyInitialState = {
  isLoadingProperty: false,
  isListOver: false as boolean | number,
  isEmptyPropertyDataWithFilters: false,
  isEmptyPropertyWithFilter: false,
  currentProperty: {} as PropertyType,

  propertyEditMode: false,
  property: {
    data: [] as PropertyType[],
    meta: {} as RootMeta,
  },
  share: {
    sharePeopleList: [] as AssignPeopleSelectValueModel[],
    selectUser: null as AssignPeopleSelectValueModel | null,
  },

  filterParams: {
    query: '',
    shared_filter: [],
  } as FilterParams,
  createProperty: {
    formStepper: propertyFormStepper as FormStepper<PropertyStepsEnum>,
    currentStep: PropertyStepsEnum.preStepPropertyType,
    propertyType: PropertySelectFormatEnum.owner,
    draftItem: null as null | PropertyType,
    isCanChangeCurrentStep: true,
  },
};
type InitialStateType = typeof propertyInitialState;

const propertySlice = createSlice({
  name: 'propertySlice',
  initialState: propertyInitialState as InitialStateType,
  reducers: {
    // ------------------ common ------------------
    setLoadingProperty(state, action: PayloadAction<{ isLoading: boolean }>) {
      state.isLoadingProperty = action.payload.isLoading;
    },
    clearPropertyFilters(state) {
      state.filterParams = { ...state.filterParams, query: '' };
    },

    // ------------------ main page ------------------
    setPropertyFilterParams(state, action: PayloadAction<{ params: FilterParams }>) {
      state.filterParams = action.payload.params;
    },
    removeCurrentProperty(state, action: PayloadAction<{ propertyID: number }>) {
      const index = state.property.data.findIndex((property) => property.id === action.payload.propertyID);
      if (index !== -1) state.property.data.splice(index, 1);

      if (
        !state.property.data.length &&
        (!!state.filterParams.shared_filter.length || !!state.filterParams.query)
      ) {
        state.isEmptyPropertyWithFilter = true;
      }
    },
    setPropertyDataWithNewPosition(state, action: PayloadAction<{ newPositionData: PropertyType[] }>) {
      state.property.data = action.payload.newPositionData;
    },
    isEmptyDataPropertyWithFilter(
      state,
      action: PayloadAction<{
        filters: Pick<GetHubsParams, 'shared_filter' | 'query'>;
        propertyData: PropertyType[];
      }>,
    ) {
      const { shared_filter, query } = action.payload.filters;
      state.isEmptyPropertyWithFilter =
        (!!shared_filter?.length || !!query) && !action.payload.propertyData.length;
    },
    clearPropertyState(state) {
      state.property = propertyInitialState.property;
    },
    // ------------------ view property ------------------
    setEditMode(state, action: PayloadAction<{ editMode: boolean }>) {
      state.propertyEditMode = action.payload.editMode;
    },
    // ------------------ gallery property ------------------
    addGalleryPhoto(state, action: PayloadAction<{ newGalleryPhotos: MediaType[] }>) {
      state.currentProperty.photos = action.payload.newGalleryPhotos;
    },
    updateGalleryCoverPhoto(state, action: PayloadAction<{ coverPhoto: MediaType }>) {
      state.currentProperty.cover_photo = action.payload.coverPhoto;
    },
    removeGalleryCoverPhoto(
      state,
      action: PayloadAction<{ deleteCoverID: number; isCover: boolean | undefined }>,
    ) {
      const deleteCoverIndex = state.currentProperty.photos.findIndex(
        (photo) => photo.id === action.payload.deleteCoverID,
      );
      if (deleteCoverIndex !== -1) state.currentProperty.photos.splice(deleteCoverIndex, 1);
      if (!!state.currentProperty.photos.length) {
        const { photos } = changeCoverPhoto(state.currentProperty.photos);

        const hasCoverPhoto = state.currentProperty.photos.filter((photo) => photo.is_cover);

        state.currentProperty.photos = [...photos];
        if (!hasCoverPhoto.length) {
          state.currentProperty.cover_photo = photos.length ? photos[photos.length - 1] : null;
        }
      } else state.currentProperty.cover_photo = null;
    },
    // ------------------ files property ------------------
    addPropertyDocuments(state, action: PayloadAction<{ newDocuments: MediaType[] }>) {
      state.currentProperty.documents = action.payload.newDocuments;
    },
    // ------------------ share property ------------------
    setAssignPeopleLists(state, action: PayloadAction<{ peopleLists: AssignPeopleSelectValueModel[] }>) {
      state.share.sharePeopleList = action.payload.peopleLists;
    },
    setSelectedUser(state, action: PayloadAction<{ selectUser: AssignPeopleSelectValueModel | null }>) {
      state.share.selectUser = action.payload.selectUser;
    },
    addShareUser(state, action: PayloadAction<{ shareUser: AssignPeopleSelectValueModel }>) {
      state.currentProperty.users.push(action.payload.shareUser);
    },
    deletePropertyShareUser(state, action: PayloadAction<{ shareUserID: number }>) {
      const index = state.currentProperty.users.findIndex(
        (user: ItemUserModel) => user.id === action.payload.shareUserID,
      );
      if (index !== -1) state.currentProperty.users.splice(index, 1);
    },
    updateShareUser(
      state,
      action: PayloadAction<{ userRole: AssignPeoplePermissionsEnum; shareUserID: number }>,
    ) {
      const index = state.currentProperty.users.findIndex(
        (user: ItemUserModel) => user.id === action.payload.shareUserID,
      );
      if (index !== -1) state.currentProperty.users[index].role = action.payload.userRole;
    },
    // ------------------ create property ------------------
    setPropertyCreateType: (state, action: PayloadAction<PropertySelectFormatEnum>) => {
      state.createProperty.propertyType = action.payload;
    },

    setPropertyCoverPhoto: (state, action: PayloadAction<{ cover_photo: MediaType | null }>) => {
      if (state.createProperty.draftItem) {
        state.createProperty.draftItem.cover_photo = action.payload.cover_photo;
      }
    },
    setPropertyCreateDraftItem: (state, action: PayloadAction<any | null>) => {
      state.createProperty.draftItem = action.payload;
      if (action.payload) {
        state.createProperty.propertyType = action.payload.type;
      }
    },
    updatePropertyDraftItem: (state, action: PayloadAction<any | null>) => {
      state.createProperty.draftItem = { ...state.createProperty.draftItem, ...action.payload };
    },
    setPropertyCreateCurrentStep: (state, action: PayloadAction<PropertyStepsEnum>) => {
      state.createProperty.currentStep = action.payload;
      state.createProperty.isCanChangeCurrentStep = true;
    },
    changePropertyCreateFormStepperFromDraftItem: (
      state,
      action: PayloadAction<RootPropertyItemsDataStep[]>,
    ) => {
      const enabledSteps = new Set([PropertyStepsEnum.preStepPropertyType]);

      action.payload.forEach(({ step }) => {
        const currentStep = state.createProperty.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });

      const lastStepInPayload = action.payload[action.payload.length - 1];

      if (lastStepInPayload) {
        state.createProperty.currentStep =
          propertyCreateFormStepper[
            propertyCreateFormStepper.findIndex((item) => item === lastStepInPayload.step) + 1
          ];
      }
    },
    setPropertyCreateFormStepPassed: (
      state,
      action: PayloadAction<{ step: PropertyStepsEnum; isPassed?: boolean }>,
    ) => {
      const { step, isPassed = true } = action.payload;
      if (state.createProperty.formStepper[step]) {
        state.createProperty.formStepper[step].isPassed = isPassed;
      }
    },
    setPropertyCreateFormStepDisabled: (
      state,
      action: PayloadAction<{ step: PropertyStepsEnum; isDisabled?: boolean }>,
    ) => {
      const { step, isDisabled = true } = action.payload;
      if (state.createProperty.formStepper[step]) {
        state.createProperty.formStepper[step].isDisabled = isDisabled;
      }
    },
    setPropertyCreateFormStepSkip: (
      state,
      action: PayloadAction<{ step: PropertyStepsEnum; isSkip?: boolean }>,
    ) => {
      const { step, isSkip = true } = action.payload;
      if (state.createProperty.formStepper[step]) {
        state.createProperty.formStepper[step].isSkip = isSkip;
      }
    },
    setPropertyCreateCanChangeCurrentStep: (state, action: PayloadAction<boolean>) => {
      state.createProperty.isCanChangeCurrentStep = action.payload;
    },
    changePropertyCreateFormStepperPassedDisableSteps: (
      state,
      action: PayloadAction<{
        enabledSteps: PropertyStepsEnum[];
        passedStep: RootPropertyItemsDataStep[];
      }>,
    ) => {
      const enabledSteps = new Set(action.payload.enabledSteps);

      action.payload.passedStep.forEach(({ step }) => {
        const currentStep = state.createProperty.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });
    },

    resetCreateStore: (state) => {
      state.createProperty = {
        formStepper: propertyFormStepper,
        currentStep: PropertyStepsEnum.preStepPropertyType,
        propertyType: PropertySelectFormatEnum.owner,
        draftItem: null,
        isCanChangeCurrentStep: true,
      };
    },

    setCurrentProperty(state, action: PayloadAction<{ property: PropertyType }>) {
      state.currentProperty = action.payload.property;
    },
    updatePropertyCoverPhotoWS(
      state,
      action: PayloadAction<{ newCoverPhoto: MediaType; propertyID: number }>,
    ) {
      const index = state.property.data.findIndex((property) => property.id === action.payload.propertyID);

      if (index !== -1) state.property.data[index].cover_photo = action.payload.newCoverPhoto;
    },
  },
  extraReducers: ({ addCase }) => {
    // ------------------ main page ------------------
    addCase(getProperties.fulfilled, (state, { payload }) => {
      if (payload.meta.current_page === 1) {
        state.property.data = payload.data;
        state.property.meta = payload.meta;
        state.isListOver = isListOver(payload);
      } else {
        state.property.data.push(...payload.data);
        state.property.meta = payload.meta;
        state.isListOver = isListOver(payload);
      }
    });
    addCase(updatePropertyWS.fulfilled, (state, action: PayloadAction<{ propertyWS: PropertyType }>) => {
      if (!!state.property.data.length) {
        const index = state.property.data.findIndex(
          (property) => property.id === action.payload.propertyWS.id,
        );

        if (index !== -1) state.property.data[index] = action.payload.propertyWS;
      }
    });
    addCase(addPropertyWS.fulfilled, (state, action: PayloadAction<{ addPropertyWS: PropertyType }>) => {
      const index = state.property.data.findIndex(
        (property) => property.id === action.payload.addPropertyWS.id,
      );

      if (index !== -1) {
        state.property.data[index] = action.payload.addPropertyWS;
      } else {
        state.property.data = !!state.property.data.length
          ? [action.payload.addPropertyWS, ...state.property.data]
          : [action.payload.addPropertyWS];
      }
    });
    addCase(
      acceptedPropertyWS.fulfilled,
      (state, action: PayloadAction<{ acceptedPropertyWS: PropertyType }>) => {
        const index = state.property.data.findIndex(
          (education) => education.id === action.payload.acceptedPropertyWS.id,
        );
        state.property.data.splice(index, 1);

        const pending = state.property.data.filter(
          (property) => property.current.confirm_status === UserConfirmStatusEnum.pending,
        );
        const general = state.property.data.filter(
          (property) => property.current.confirm_status !== UserConfirmStatusEnum.pending,
        );

        state.property.data = [...pending, action.payload.acceptedPropertyWS, ...general];
      },
    );
  },
});

export const {
  setPropertyFilterParams,
  setLoadingProperty,
  resetCreateStore,
  setPropertyCreateType,
  setPropertyCreateFormStepSkip,
  setPropertyCreateFormStepPassed,
  setPropertyCreateFormStepDisabled,
  setPropertyCreateCurrentStep,
  setPropertyCreateCanChangeCurrentStep,
  setPropertyCreateDraftItem,
  updatePropertyDraftItem,
  changePropertyCreateFormStepperPassedDisableSteps,
  changePropertyCreateFormStepperFromDraftItem,
  setPropertyCoverPhoto,
  setCurrentProperty,
  removeCurrentProperty,
  setPropertyDataWithNewPosition,
  isEmptyDataPropertyWithFilter,
  clearPropertyState,
  updatePropertyCoverPhotoWS,
  updateGalleryCoverPhoto,
  setEditMode,
  addPropertyDocuments,
  clearPropertyFilters,
  addGalleryPhoto,
  removeGalleryCoverPhoto,
  setAssignPeopleLists,
  setSelectedUser,
  addShareUser,
  deletePropertyShareUser,
  updateShareUser,
} = propertySlice.actions;

export const propertyReducer = propertySlice.reducer;
